"use client";

import NextLink from "next/link";
import { useRef } from "react";

import { useToast } from "@/components/ui/toast";
import { useLeaveFor } from "@/lib/navigation/use-leave-for";

/** The prototype's words for the host (`catToast`, outside the internal flow). */
export const ADOPT_SUGGESTION_TOAST = "Vamos começar a sua lista com esta curadoria";

/**
 * "Quero essa lista": the toast that says the list starts from this curation, then the way on.
 * Still a real link, so a click with a modifier (new tab, new window) is left to the browser.
 *
 * **The wait is not this component's any more.** It used to keep a 700ms timer of its own —
 * borrowed from the prototype's `adotarLista` — which was shorter than the toast's own dwell, so
 * it navigated while the message was still on screen. Reported 2026-10-09: "não deu tempo de
 * ler". A fourth timing, in a fourth place, after three toast hosts had just been reduced to one.
 *
 * `useLeaveFor` is the only way out of a screen now: it asks the toast host when the message has
 * been read *and faded*, and refreshes before pushing. One definition, so there is nothing here
 * to drift.
 */
export function AdoptSuggestionLink({
  href,
  className,
  children,
  onAdopt,
}: {
  href: string;
  className?: string;
  /** Called once, when the press takes the person on: where the analytics hear of the adoption. */
  onAdopt?: () => void;
  children: React.ReactNode;
}) {
  const leaveFor = useLeaveFor();
  const { showToast } = useToast();
  /** A second press while the first is on its way changes nothing — and says nothing twice. */
  const isLeaving = useRef(false);

  return (
    <NextLink
      href={href}
      className={className}
      onClick={(event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (isLeaving.current) return;

        isLeaving.current = true;
        onAdopt?.();
        showToast(ADOPT_SUGGESTION_TOAST, { variant: "success" });
        void leaveFor(href);
      }}
    >
      {children}
    </NextLink>
  );
}
